/**
 * Represents a custom HTML element for displaying one or more product cards.
 *
 * The ProductCard component fetches product data via AJAX and renders product cards dynamically.
 * It supports multiple product handles, custom card width, quick add-to-cart modals, and integration with Swym Wishlist.
 *
 * @class
 * @extends HTMLElement
 *
 * @example
 * <product-card data-handle="product-handle" data-width="300px"></product-card>
 * <product-card data-handle="handle1,handle2"></product-card>
 *
 * @property {string|string[]} productHandle - The handle(s) of the product(s) to display.
 * @property {string} cardWidth - The maximum width of the product card.
 * @property {HTMLElement} [modal] - Reference to the currently open modal, if any.
 *
 * @method connectedCallback - Lifecycle method called when the element is added to the DOM.
 * @method init - Initializes the component.
 * @method setData - Sets product handle and card width from data attributes.
 * @method setStyles - Applies styles and layout classes to the component.
 * @method setHandle - Normalizes productHandle to an array.
 * @method initSwymWishlist - Initializes Swym Wishlist integration if available.
 * @method getProduct - Fetches product card HTML and renders it.
 * @method handleQuickAddToCart - Sets up event listeners for quick add-to-cart modals.
 * @method closeModal - Closes the currently open modal.
 * @method setCloseEvent - Sets up event listener for closing the modal.
 */

class ProductCard extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.setData();
    this.setHandle();
    this.setStyles();
    this.renderSkeletons();
    this.getProduct();
  }

  setData() {
    this.productHandle = this.dataset.handle;
    this.cardWidth = this.dataset.width || '250px';
    // optional: comma-separated list of variant ids aligned with handles
    this.variantIds = this.dataset.variant || '';
  }

  setStyles() {
    this.className = '';
    this.classList.add('grid', 'grid-cols-2', 'md:grid-cols-3', 'gap-3');
  }

  renderSkeletons() {
    try {
      const count = Array.isArray(this.productHandle) ? this.productHandle.length : 3;
      const skeletonItems = Array.from({ length: count }).map(() => `
        <div class="bppg-skeleton-card overflow-hidden">
          <div class="bppg-skeleton bppg-skeleton-image"></div>
          <div class="p-2 md:p-[0.625rem]">
            <div class="bppg-skeleton bppg-skeleton-line" style="width: 80%"></div>
            <div class="bppg-skeleton bppg-skeleton-price" style="width: 40%"></div>
          </div>
        </div>
      `).join('');
      this.innerHTML = skeletonItems;
    } catch (_) {
      // no-op if skeleton render fails
    }
  }

  setHandle() {
    if (this.productHandle.includes(',')) {
      this.productHandle = this.productHandle.split(',');
    } else {
      this.productHandle = [this.productHandle];
    }

    // normalize variant ids array to align index-wise with handles
    if (typeof this.variantIds === 'string' && this.variantIds.length > 0) {
      if (this.variantIds.includes(',')) {
        this.variantIds = this.variantIds.split(',');
      } else {
        this.variantIds = [this.variantIds];
      }
    } else {
      this.variantIds = [];
    }

    // pad/truncate to match handles length (extra ids are ignored, missing become empty)
    if (this.variantIds.length < this.productHandle.length) {
      this.variantIds = [
        ...this.variantIds,
        ...Array(this.productHandle.length - this.variantIds.length).fill(''),
      ];
    } else if (this.variantIds.length > this.productHandle.length) {
      this.variantIds = this.variantIds.slice(0, this.productHandle.length);
    }
  }

  initSwymWishlist(rootContainer = this) {
    // Swym expects a selector string, not an element. Build a safe selector.
    const containerSelector = (() => {
      try {
        if (rootContainer && rootContainer instanceof HTMLElement) {
          if (rootContainer.id) return `#${rootContainer.id}`;
          // fallback to tag selector for product-card
          if (rootContainer.tagName && rootContainer.tagName.toLowerCase() === 'product-card') return 'product-card';
        }
      } catch (_) {}
      // final fallback: scope to document
      return null;
    })();

    const init = (swat) => {
      try {
        if (swat && typeof swat.initializeActionButtons === 'function') {
          if (containerSelector) swat.initializeActionButtons(containerSelector); else swat.initializeActionButtons();
        } else if (window._swat && typeof window._swat.initializeActionButtons === 'function') {
          if (containerSelector) window._swat.initializeActionButtons(containerSelector); else window._swat.initializeActionButtons();
        }
      } catch (e) {
        console.warn('Swym initializeActionButtons error', e);
      }
    };

    // Immediate attempt
    if (window._swat) init(window._swat);

    // Next frame + slight delays to avoid race conditions
    try {
      requestAnimationFrame(() => setTimeout(() => init(window._swat), 0));
      setTimeout(() => init(window._swat), 150);
      setTimeout(() => init(window._swat), 500);
    } catch (_) {}

    // If Swym loads later, queue callback
    try {
      window.SwymCallbacks = window.SwymCallbacks || [];
      window.SwymCallbacks.push((swat) => init(swat));
    } catch (e) {
      console.warn('SwymCallbacks queue push failed', e);
    }
  }

  getProduct() {
    if (!this.productHandle) {
      console.warn('Product handle is not set for ProductCard component');
      return;
    }

    Promise.all(
      this.productHandle.map((handle, index) => {
        const variantId = (this.variantIds && this.variantIds[index]) ? this.variantIds[index].toString().trim() : '';
        const variantQuery = variantId ? `&variant=${encodeURIComponent(variantId)}` : '';
        const url = `/products/${handle}?view=card${variantQuery}`;
        return fetch(url)
          .then((response) => {
            if (!response.ok) {
              console.warn(`Product ${handle} returned status ${response.status}`);
              return null;
            }
            return response.text();
          });
      })
    )
      .then((htmlArray) => {
        // Filter out null responses (failed products)
        const validCards = htmlArray.filter(Boolean);
        
        if (validCards.length === 0) {
          // No valid products found, hide the component
          console.warn('No valid products found to display');
          this.style.display = 'none';
          return;
        }
        
        // Replace skeletons with loaded cards
        this.innerHTML = validCards.join('');
        // Re-apply layout classes (if needed) after HTML replacement
        this.setStyles();
        // Initialize product-form custom elements
        this.initProductForms();
        // Initialize Swym wishlist buttons within this card
        this.initSwymWishlist();
        this.handleQuickAddToCart();
        this.setCloseEvent();
      })
      .catch((error) => {
        console.error('Error fetching product card HTML:', error);
        this.style.display = 'none';
      });
  }

  initProductForms() {
    // Find all product-form elements within this component
    const productForms = this.querySelectorAll('product-form');
    
    // The custom element should auto-initialize when added to DOM,
    // but we'll ensure the definition is loaded first
    if (productForms.length > 0 && !customElements.get('product-form')) {
      // Wait for product-form to be defined if it's not yet
      customElements.whenDefined('product-form').then(() => {
        // Custom element is now defined, elements should auto-upgrade
      }).catch(() => {
        console.warn('product-form custom element not defined');
      });
    }
  }

  handleQuickAddToCart() {
    // Global handler in rca-quick-view-popup.js now handles all modal triggers
    // Just need to re-initialize Swym for dynamically loaded content
    this.querySelectorAll('button[data-modal]').forEach((button) => {
      button.addEventListener('click', () => {
        const modal = this.querySelector(button.getAttribute('data-modal'));
        if (modal) {
          // Re-initialize Swym inside the modal content
          this.initSwymWishlist(modal);
        }
      });
    });
  }

  closeModal() {
    if (this.modal) {
      this.modal.removeAttribute('open');
      document.body.classList.remove('overflow-hidden');
    }
  }

  setCloseEvent() {
    const closeButton = this.querySelector('.js-quick-view-close');
    if (closeButton) {
      closeButton.addEventListener('click', () => {
        this.closeModal();
      });
    }
  }
}

customElements.define('product-card', ProductCard);

class BlogPostProductGrid {
  constructor() {
    // in-memory cache for this page load (no TTL)
    // maps sku -> { handle, variantId }
    this.skuToHandleCache = new Map();

    // in-memory cache for collection to handles (no TTL)
    // key: `${collectionHandle}:${limit}` -> [handles]
    this.collectionToHandlesCache = new Map();

    // session cache (with TTL)
    this.SESSION_KEY = 'bppg_sku_handle_cache_v1';
    this.COLLECTION_SESSION_KEY = 'bppg_collection_cache_v1';
    this.CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

    // concurrency config for search requests
    this.MAX_CONCURRENCY = 3;        // bounded concurrency for batches

    // allow bypassing cache & disable cache in theme editor
    const qs = new URLSearchParams(location.search);
    this.cacheEnabled = !(window.Shopify && Shopify.designMode) && !qs.has('noCache');

    // load session cache object { [sku]: { handle, variantId, ts } }
    this.sessionCache = this.loadSessionCache();

    // load collection session cache object { [key]: { handles, ts } }
    this.collectionSessionCache = this.loadCollectionSessionCache();

    this.init();
  }

  init() {
    // Only run on blog article pages
    const articleContent = document.querySelector('.article-template__content');
    if (!articleContent) return;

    this.injectGlobalSkeletonStyles();
    this.parseAndReplaceShortcodes();
  }

  parseAndReplaceShortcodes() {
    const contentElements = document.querySelectorAll('.article-template__content');
    
    contentElements.forEach(element => {
      const content = element.innerHTML;
      const productShortcodePattern = /\[productCarousel\s+skus=([^\]]+)\]/g;
      const collectionShortcodePattern = /\[collectionCarousel\s+collectionHandle=([^\s\]]+)\s*(limit=(\d+))?\]/g;
      const productMatches = [...content.matchAll(productShortcodePattern)];
      const collectionMatches = [...content.matchAll(collectionShortcodePattern)];
      
      if (productMatches.length === 0 && collectionMatches.length === 0) return;
      
      productMatches.forEach(match => {
        const fullMatch = match[0];
        const skusString = match[1];
        const skus = skusString.split(',').map(sku => sku.trim());
        
        // Create placeholder div
        const placeholder = document.createElement('div');
        placeholder.className = 'product-grid-placeholder grid grid-cols-2 md:grid-cols-3 gap-3';
        placeholder.dataset.skus = skus.join(',');
        placeholder.innerHTML = this.buildSkeletonItems(skus.length || 3);
        
        // Replace shortcode with placeholder
        element.innerHTML = element.innerHTML.replace(fullMatch, placeholder.outerHTML);
      });

      collectionMatches.forEach(match => {
        const fullMatch = match[0];
        const collectionHandle = match[1];
        const limit = match[3] ? parseInt(match[3], 10) : 20;

        const placeholder = document.createElement('div');
        placeholder.className = 'collection-grid-placeholder grid grid-cols-2 md:grid-cols-3 gap-3';
        placeholder.dataset.collectionHandle = collectionHandle;
        placeholder.dataset.limit = String(limit);
        placeholder.innerHTML = this.buildSkeletonItems(limit || 6);

        element.innerHTML = element.innerHTML.replace(fullMatch, placeholder.outerHTML);
      });
      
      // Now process all placeholders
      this.processPlaceholders();
      this.processCollectionPlaceholders();
    });
  }

  injectGlobalSkeletonStyles() {
    try {
      const styleId = 'bppg-skeleton-global-styles';
      if (document.getElementById(styleId)) return;
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .bppg-skeleton { position: relative; overflow: hidden; background: #e5e7eb; }
        .bppg-skeleton::after { content: ""; position: absolute; inset: 0; transform: translateX(-100%); background: linear-gradient(90deg, rgba(255,255,255,0), rgba(255,255,255,.6), rgba(255,255,255,0)); animation: bppg-skeleton-shimmer 1.2s infinite; }
        @keyframes bppg-skeleton-shimmer { 100% { transform: translateX(100%); } }
        .bppg-skeleton-card { background: #ffffff; border-radius: 10px; }
        .bppg-skeleton-image { padding-bottom: 120%; }
        .bppg-skeleton-line { height: 16px; margin-top: 8px; border-radius: 8px; }
        .bppg-skeleton-price { height: 20px; width: 40%; margin-top: 10px; border-radius: 10px; }
      `;
      document.head.appendChild(style);
    } catch (_) { /* no-op */ }
  }

  buildSkeletonItems(count) {
    const safeCount = Math.max(1, Math.min(24, parseInt(count, 10) || 3));
    return Array.from({ length: safeCount }).map(() => `
      <div class="bppg-skeleton-card overflow-hidden">
        <div class="bppg-skeleton bppg-skeleton-image"></div>
        <div class="p-2 md:p-[0.625rem]">
          <div class="bppg-skeleton bppg-skeleton-line" style="width: 80%"></div>
          <div class="bppg-skeleton bppg-skeleton-price" style="width: 40%"></div>
        </div>
      </div>
    `).join('');
  }

  async processPlaceholders() {
    const placeholders = document.querySelectorAll('.product-grid-placeholder');
    
    for (const placeholder of placeholders) {
      const skus = placeholder.dataset.skus.split(',');
      const selections = await this.mapSkusToHandles(skus); // [{ handle, variantId }]
      
      if (selections.length > 0) {
        const handles = selections.map(s => s.handle);
        const variantIds = selections.map(s => s.variantId || '');
        // Create product-card element
        const productCard = document.createElement('product-card');
        productCard.dataset.handle = handles.join(',');
        productCard.dataset.variant = variantIds.join(',');
        productCard.dataset.width = '300px';
        
        // Replace placeholder with product card
        placeholder.replaceWith(productCard);
      } else {
        // remove placeholder silently if nothing found
        placeholder.remove();
      }
    }
  }

  async processCollectionPlaceholders() {
    const placeholders = document.querySelectorAll('.collection-grid-placeholder');
    for (const placeholder of placeholders) {
      const collectionHandle = placeholder.dataset.collectionHandle;
      const limit = Math.max(1, Math.min(50, parseInt(placeholder.dataset.limit, 10) || 20));
      const cacheKey = `${collectionHandle}:${limit}`;

      let handles = [];
      // in-memory
      if (this.collectionToHandlesCache.has(cacheKey)) {
        handles = this.collectionToHandlesCache.get(cacheKey) || [];
      }

      // session cache with TTL
      if (!handles.length && this.cacheEnabled) {
        const entry = this.collectionSessionCache[cacheKey];
        if (entry && (this.now() - (entry.ts || 0) <= this.CACHE_TTL_MS)) {
          handles = Array.isArray(entry.handles) ? entry.handles : [];
          this.collectionToHandlesCache.set(cacheKey, handles);
        }
      }

      if (!handles.length) {
        try {
          // If limit <= 50, single page is enough. Else, paginate similarly.
          if (limit <= 50) {
            const products = await this.fetchCollectionProducts(collectionHandle, limit, 1);
            handles = products.map(p => p.handle).filter(Boolean);
          } else {
            const perPage = 50;
            let page = 1;
            while (handles.length < limit) {
              const batch = await this.fetchCollectionProducts(collectionHandle, perPage, page);
              if (!batch.length) break;
              handles.push(...batch.map(p => p.handle).filter(Boolean));
              page += 1;
            }
            handles = handles.slice(0, limit);
          }

          // set caches
          this.collectionToHandlesCache.set(cacheKey, handles);
          if (this.cacheEnabled) {
            this.collectionSessionCache[cacheKey] = { handles, ts: this.now() };
            this.saveCollectionSessionCache();
          }
        } catch (e) {
          console.warn('Collection fetch failed', collectionHandle, e);
        }
      }

      if (handles.length > 0) {
        const productCard = document.createElement('product-card');
        productCard.dataset.handle = handles.join(',');
        productCard.dataset.width = '300px';
        placeholder.replaceWith(productCard);
      } else {
        // remove placeholder silently if nothing found
        placeholder.remove();
      }
    }
  }

  // ---- helpers: cache, timing, fetch ----
  now() { return Date.now(); }

  loadSessionCache() {
    try {
      const raw = sessionStorage.getItem(this.SESSION_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (e) {
      console.warn('Session cache load failed', e);
      return {};
    }
  }

  loadCollectionSessionCache() {
    try {
      const raw = sessionStorage.getItem(this.COLLECTION_SESSION_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (e) {
      console.warn('Collection session cache load failed', e);
      return {};
    }
  }

  saveSessionCache() {
    try {
      sessionStorage.setItem(this.SESSION_KEY, JSON.stringify(this.sessionCache));
    } catch (e) {
      // storage can fail in safari private etc.
      console.warn('Session cache save failed', e);
    }
  }

  saveCollectionSessionCache() {
    try {
      sessionStorage.setItem(this.COLLECTION_SESSION_KEY, JSON.stringify(this.collectionSessionCache));
    } catch (e) {
      console.warn('Collection session cache save failed', e);
    }
  }

  getFromSessionCache(sku) {
    if (!this.cacheEnabled) return null;
    const entry = this.sessionCache[sku];
    if (!entry) return null;
    if (this.now() - (entry.ts || 0) > this.CACHE_TTL_MS) return null; // expired
    if (!entry.handle) return null;
    return { handle: entry.handle, variantId: entry.variantId || '' };
  }

  setSessionCache(sku, selection) {
    if (!this.cacheEnabled) return;
    this.sessionCache[sku] = {
      handle: selection?.handle || null,
      variantId: selection?.variantId || '',
      ts: this.now(),
    };
    this.saveSessionCache();
  }

  sleep(ms) { return new Promise(r => setTimeout(r, ms)); }

  async fetchWithBackoff(url, options = {}, retries = 3) {
    for (let attempt = 0; attempt <= retries; attempt++) {
      const res = await fetch(url, Object.assign({ headers: { 'Accept': 'application/json' } }, options));
      if (res.status === 429) {
        const ra = parseFloat(res.headers.get('Retry-After'));
        const wait = isNaN(ra) ? (300 * Math.pow(2, attempt) + Math.random() * 200) : ra * 1000;
        await this.sleep(wait);
        continue;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status} for ${url}`);
      return res;
    }
    throw new Error(`Too many retries for ${url}`);
  }

  async searchProductBySku(sku) {
    const url = `/search/suggest.json?q=${encodeURIComponent(sku)}&resources[type]=product&resources[options][fields]=variants.sku`;
    const res = await this.fetchWithBackoff(url);
    const data = await res.json();
    const products = data?.resources?.results?.products;
    return Array.isArray(products) && products.length > 0 ? products[0] : null;
  }

  async fetchCollectionProducts(collectionHandle, limit, page = 1) {
    const url = `/collections/${encodeURIComponent(collectionHandle)}/products.json?limit=${limit}&page=${page}`;
    const res = await this.fetchWithBackoff(url);
    const data = await res.json();
    return Array.isArray(data?.products) ? data.products : [];
  }

  async mapSkusToHandles(skus) {
    // normalize inputs and preserve order
    const normalized = skus.map(s => (s || '').toString().trim()).filter(Boolean);
    const selectionsBySku = new Map(); // sku -> { handle, variantId }

    // 1) in-memory hits first (same page-load)
    for (const sku of normalized) {
      if (this.skuToHandleCache.has(sku)) {
        const sel = this.skuToHandleCache.get(sku);
        if (sel && sel.handle) selectionsBySku.set(sku, sel);
      }
    }

    // 2) session cache with TTL
    for (const sku of normalized) {
      if (selectionsBySku.has(sku)) continue;
      const cached = this.getFromSessionCache(sku);
      if (cached) {
        selectionsBySku.set(sku, cached);
        this.skuToHandleCache.set(sku, cached);
      }
    }

    // set of remaining SKUs to find
    const remaining = normalized.filter(sku => !selectionsBySku.has(sku));
    if (remaining.length === 0) {
      return normalized.map(sku => selectionsBySku.get(sku)).filter(Boolean);
    }

    // 3) Search for remaining SKUs using the search suggest API
    // Batch search requests with bounded concurrency
    const searchPromises = remaining.map(sku => 
      this.searchProductBySku(sku)
        .then(product => ({ sku, product }))
        .catch(error => {
          console.warn(`Search failed for SKU ${sku}:`, error);
          return { sku, product: null };
        })
    );

    // Process searches with bounded concurrency
    const batchSize = this.MAX_CONCURRENCY;
    for (let i = 0; i < searchPromises.length; i += batchSize) {
      const batch = searchPromises.slice(i, i + batchSize);
      const results = await Promise.all(batch);
      
      for (const { sku, product } of results) {
        if (product && product.handle) {
          // For now, we don't have variant ID from the search API response
          // The product card will use the default variant
          const selection = { handle: product.handle, variantId: '' };
          selectionsBySku.set(sku, selection);
          this.skuToHandleCache.set(sku, selection);
          this.setSessionCache(sku, selection);
        } else {
          console.warn(`Product with SKU ${sku} not found`);
          // cache negative lookups only in-memory for this run
          this.skuToHandleCache.set(sku, null);
        }
      }
    }

    // return selections in the same order as input SKUs, ignoring unfound
    return normalized.map(sku => selectionsBySku.get(sku)).filter(Boolean);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new BlogPostProductGrid());
} else {
  new BlogPostProductGrid();
}
